<div class="charging-options" data-testid="charging-options-container">
  <!-- Fast charge limit -->
  @if (selectedCar?.FastChargePower !== 0) {
    <app-modal-option-panel>
      <h3 panel-title>{{ languageService.languageJSON?.CarSelectorDialog_FastChargeLimit }}</h3>
      <span panel-side class="slider-value">{{ fastChargeLimit | number:'1.0-0' }}%</span>
      <p class="modal-option-panel__description">{{ languageService.languageJSON?.CarSelectorDialog_FastChargeLimit_Description }}</p>
      <app-ev-slider data-testid="charging-options-fast-charge-slider" [min]="fastChargeLimitMin" [max]="fastChargeLimitMax"
        [digit]="0" [altString]="'fast charge limit'" [value]="fastChargeLimit"
        (valueDragEvent)="fastChargeLimitChange.emit($event)">
      </app-ev-slider>
      <div class="slider-indicators">
        <div>{{ fastChargeLimitMin }}%</div>
        <div>{{ fastChargeLimitMax }}%</div>
      </div>
    </app-modal-option-panel>
  }

  <!-- Battery / Range adjustment (state of health) -->
  <app-modal-option-panel>
    <h3 panel-title>{{ languageService.languageJSON?.CarSelectorDialog_BatteryStateOfHealth }}</h3>
    <p class="modal-option-panel__description">{{ languageService.languageJSON?.CarSelectorDialog_BatteryStateOfHealth_Description }}</p>
    <app-ev-slider data-testid="charging-options-battery-health-slider" [min]="batteryStateOfHealthMin" [max]="batteryStateOfHealthMax"
      [digit]="0" [altString]="'battery/range adjustment'" [value]="batteryStateOfHealth"
      (valueDragEvent)="batteryStateOfHealthChange.emit($event)">
    </app-ev-slider>
    @if (selectedCar) {
      <div class="vehicle-row">
        <div class="vehicle-tile-property">
          <img [src]="'assets/icons/light/ui/vehicle_settings_icon_maxbattery.svg'" alt="battery health" />
          <span>{{ batteryStateOfHealth }}%</span>
        </div>
        <div class="vehicle-tile-property">
          <img [src]="'assets/icons/light/ui/vehicle_settings_icon_maxrange.svg'" alt="range" />
          <span [hidden]="settingsService.getUnit().Distance!=='metric'">
            {{ selectedCar.FactoryRange * batteryStateOfHealth / 100 }} km ({{ selectedCar.FactoryRangeSource }})
          </span>
          <span [hidden]="settingsService.getUnit().Distance!=='imperial'">
            {{ selectedCar.FactoryRange * 0.621371192 * batteryStateOfHealth / 100 | valueInteger }} mi ({{ selectedCar.FactoryRangeSource }})
          </span>
        </div>
      </div>
    }
  </app-modal-option-panel>

  <!-- Charger types -->
  <app-modal-option-panel>
    <h3 panel-title>{{ languageService.languageJSON?.CarSelectorDialog_ChargerTypes }}</h3>
    <p class="modal-option-panel__description">{{ languageService.languageJSON?.CarSelectorDialog_ChargerTypes_Description }}</p>
    <div class="charger-types-outer">
      <div class="charger-types-grid">
        @for (charger of chargerTypes; track charger.name; let i = $index) {
          <button type="button" class="charger-type ev-plain-button" [attr.data-testid]="'charging-options-charger-type-' + i" [ngClass]="{ active: selectedChargerTypes[i] }"
            (click)="toggleChargerType(i)">
            <span class="charger-icon">
              <span [appSvgRecolor]="'/assets/icons/dark/plug/' + charger.icon" [recolorColor]="plugIconColor()"></span>
            </span>
            <span class="charger-name">{{ charger.name }}</span>
          </button>
        }
      </div>
    </div>
  </app-modal-option-panel>

  <!-- Charging stations settings (widget: stationlists / operatorlists) -->
  @if (stationListsEnabled || operatorListsEnabled) {
    <app-modal-option-panel>
      <h3 panel-title>{{ languageService.languageJSON?.SettingsDialog_ChargingStationsSettings }}</h3>

      @if (stationListsEnabled) {
        <div class="cs-settings-group">
          <span class="cs-label">{{ languageService.languageJSON?.Global_ChargingStations }}</span>
          <div class="cs-buttons ">
            <div class="cs-button ev-button primary" data-testid="charging-options-favorite-stations-btn" [ngClass]="{'inactive': favoriteStationCount === 0}"
              [attr.tabindex]="favoriteStationCount === 0 ? -1 : 0"
              (click)="openChargingPage.emit({ page: 'stations', mode: 'favorite' })" (keydown.enter)="openChargingPage.emit({ page: 'stations', mode: 'favorite' })">
              <img src="./assets/icons/default/ui/chargingstation_favorite.svg" alt="favorite chargingstation" />
              <span>{{ favoriteStationCount }} {{ languageService.languageJSON?.Map_Popup_Favorite }}</span>
              <img class="arrow" src="./assets/icons/dark/ui/open_arrow.svg" alt="open" />
            </div>
            <div class="cs-button ev-button primary" data-testid="charging-options-disabled-stations-btn" [ngClass]="{'inactive': disabledStationCount === 0}"
              [attr.tabindex]="disabledStationCount === 0 ? -1 : 0"
              (click)="openChargingPage.emit({ page: 'stations', mode: 'disabled' })" (keydown.enter)="openChargingPage.emit({ page: 'stations', mode: 'disabled' })">
              <img src="./assets/icons/default/ui/chargingstation_disabled.svg" alt="disabled" />
              <span>{{ disabledStationCount }} {{ languageService.languageJSON?.Map_Popup_Disabled }}</span>
              <img class="arrow" src="./assets/icons/dark/ui/open_arrow.svg" alt="open" />
            </div>
          </div>
        </div>
      }

      @if (operatorListsEnabled) {
        <div class="cs-settings-group">
          <span class="cs-label">{{ languageService.languageJSON?.SettingsDialog_ChargingOperators }}</span>
          <div class="cs-buttons">
            <button type="button" class="cs-button ev-button primary ev-plain-button" data-testid="charging-options-prefer-operators-btn" (click)="openChargingPage.emit({ page: 'operators', mode: 'prefer' })">
              <img src="./assets/icons/default/ui/prefer_network_icon.svg" alt="charging operator" />
              <span>{{ favoriteOperatorCount }} {{ languageService.languageJSON?.SettingsDialog_Prefer }}</span>
              <img class="arrow" src="./assets/icons/dark/ui/open_arrow.svg" alt="open" />
            </button>
            <button type="button" class="cs-button ev-button primary ev-plain-button" data-testid="charging-options-avoid-operators-btn" (click)="openChargingPage.emit({ page: 'operators', mode: 'avoid' })">
              <img src="./assets/icons/default/ui/avoid_network_icon.svg" alt="avoid" />
              <span>{{ disabledOperatorCount }} {{ languageService.languageJSON?.SettingsDialog_Avoid }}</span>
              <img class="arrow" src="./assets/icons/dark/ui/open_arrow.svg" alt="open" />
            </button>
          </div>
        </div>
      }
    </app-modal-option-panel>
  }
</div>
